import React from "react";
import ReactEcharts from "echarts-for-react";

import { getLineData } from "../../service/LineGraphData";
import { useInterval } from "../../util/useInterval";
import LineGraphStyles from "./lineGraph.module.css";
import { getDateOfISOWeek, toISOString } from "../../util/toISOWeek";
import { getGraphAxisLabels,getWeekRegion2percDateStart } from "../../service/textRetrieval";

const LineGraph = ({
  textLiterals,
  variant,
  tooltipName,
  avgValueLabel,
  avgValueKey,
  title = "",
  subtitle = "",
}) => {
  const [isLoading, setIsLoading] = React.useState(true);
  // Update Line Graph settings based on scale
  const [width, setWidth] = React.useState(window.innerWidth);
  const [isSmallScreen, setIsSmallScreen] = React.useState(width < 1200);
  const updateDimensions = () => {
    setWidth(window.innerWidth);
    setIsSmallScreen(window.innerWidth < 1200);
  };
  React.useEffect(() => {
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  // Adding conversion from week number to date
  function parseResult(result) {
    for (const region of result) {
      for (const week of region.weeklyData) {
        const date = getDateOfISOWeek(parseInt(week.weekNum), week.year);
        week.date = date;
        week.dateString = toISOString(date);
      }
    }
    return result;
  }

  // Polling data
  const [yAxisLabel, setYAxisLabel] = React.useState("");
  const [xAxisLabel, setXAxisLabel] = React.useState("");
  const [dateStart, setDateStart] = React.useState(0);
  const [data, setData] = React.useState([]);
  const dataStartingIndex = 0;
  React.useEffect(() => {
    setIsLoading(true);
    setXAxisLabel(getGraphAxisLabels(textLiterals)[0]);
    setYAxisLabel(getGraphAxisLabels(textLiterals)[1]);
    setDateStart(getWeekRegion2percDateStart(variant, textLiterals));
    // console.debug(dateStart)
    getLineData(variant).then((result) => {
      // console.debug(result)
      setData(parseResult(result));
      setIsLoading(false);
    });
  }, [variant, textLiterals]);

  const fiveMinutes = 1000 * 60 * 5; // in milliseconds
  useInterval(() => {
    setIsLoading(true);
    getLineData(variant).then((result) => {
      setIsLoading(false);
      setData(parseResult(result));
    });
  }, fiveMinutes);

  function buildSeries(data) {
    const series = [];
    for (let i = 0; i < 8; i++) {
      series.push({
        name: (data[i] ?? {}).continent,
        type: "line",
        // symbol: "circle",
        // TODO Choose to use with nUlls?
        // connectNulls: true,
        hoverAnimation: false,
        xAxisIndex: 0,
        yAxisIndex: 0,
        datasetIndex: i,
        encode: {
          x: "dateString",
          y: avgValueKey,
        },
      });
    }
    return series;
  }
  const legendValues = data.map((val) => val.continent);
  const series = buildSeries(data);
  const dataset = data
    .map((val) => val.weeklyData)
    .map((weeklyDataPerContinent) => {
      return {
        dimensions: [
          "year",
          "weekNum",
          "numCountries",
          "numUKVUI",
          "numIsolates",
          "percOfUKVUI",
          "percOfUKVUI_2wkavg",
          "percOfUKVUI_3wkavg",
          "percOfUKVUI_4wkavg",
          "expSmoothingPerc",
          "date",
          "dateString",
        ],
        source: weeklyDataPerContinent.slice(dataStartingIndex),
      };
    });
  // console.log("series :>> ", series);
  const option = {
    title: [
      {
        text: title,
        subtext: subtitle,
        left: "center",
      },
    ],
    legend: {
      data: legendValues,
      orient: isSmallScreen ? "horizontal" : "vertical",
      type: "scroll",
      top: title === "" && subtitle === "" ? "1%" : "11%",
      right: 0,
    },
    // visualMap: {
    //   show: false,
    //   type: "continuous",
    //   min: 0,
    //   max: 1000,
    //   inRange: {
    //     color: ["#333", "red"],
    //     symbolSize: [5, 10],
    //   },
    //   dimension: "numIsolates",
    // },
    tooltip: {
      trigger: "item",
      axisPointer: {
        type: "cross",
        animation: false,
      },
      backgroundColor: "rgba(50,50,50, 0.9)",
      position: function (pos, params, el, elRect, size) {
        let obj = {};
        obj[["top", "bottom"][+(pos[1] < size.viewSize[1] / 2)]] = 10;
        obj[["left", "right"][+(pos[0] < size.viewSize[0] / 2)]] = 30;
        return obj;
      },
      formatter: function (params) {
        var dateEnd = new Date(
          (params ?? { data: { date: new Date() } }).data.date
        );
        dateEnd.setDate(dateEnd.getDate() + 6);
        var tooltip = `<p>From ${
          (params ?? { axisValueLabel: "" }).name
        } to ${toISOString(dateEnd)}</p>`;
        tooltip += `<p class=${LineGraphStyles.tooltip}>
          ${params.marker} ${params.seriesName}:<br/>
          Countries included: ${params.data.numCountries} <br/>
          Total isolates submitted: ${params.data.numIsolates} <br/>
          Number of ${tooltipName} isolates: ${params.data.numUKVUI} <br/>
          ${avgValueLabel}: ${params.data[avgValueKey]}%
          </p>`;

        return tooltip;
      },
    },
    grid: [
      {
        left: isSmallScreen ? "15%" : "10%",
        top: isSmallScreen
          ? "15%"
          : title === "" && subtitle === ""
          ? "5%"
          : "14%",
        bottom: "25%",
        right: isSmallScreen ? "5%" : "28%",
      },
    ],

    dataZoom: [
      {
        type: "inside",
        realtime: true,
        startValue:dateStart ,
        // start: 0,
        // end: 100,
        xAxisIndex: [0],
      },
      {
        type: "slider",
        show: true,
        realtime: true,
        start: 0,
        end: 100,
        top: "89%",
        handleIcon:
          "M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z",
        handleSize: "80%",
        handleStyle: {
          color: "#fff",
          shadowBlur: 3,
          shadowColor: "rgba(0, 0, 0, 0.6)",
          shadowOffsetX: 2,
          shadowOffsetY: 2,
        },
      },
    ],
    xAxis: {
      name: xAxisLabel,
      nameLocation: "middle",
      nameTextStyle: {
        align: "center",
        verticalAlign: "top",
        padding: [10, 0, 0, 0],
      },
      gridIndex: 0,
      type: "category",
    },
    yAxis: {
      name: yAxisLabel,
      gridIndex: 0,
      nameLocation: "middle",
      nameGap: 25,
      offset: -2,
      nameTextStyle: {
        padding: [0, 0, 5, 0],
      },
    },
    dataset: dataset,
    series: series,
  };

  const seeEvent = (e) => {
    console.log("e :>> ", e);
  };

  function legendChange({ selected }) {
    console.log("selected :>> ", selected);
    // const primers = [];
    // for (const primer of Object.keys(selected)) {
    //   if (selected[primer]) {
    //     primers.push(primer);
    //   }
    // }
    // setPrimers(primers);
  }

  const dataZoomChange = (e) => {
    // console.log("e :>> ", e);
  };

  let onEvents = {
    click: seeEvent,
    legendselectchanged: legendChange,
    dataZoom: dataZoomChange,
  };
  return (
    <ReactEcharts showLoading={isLoading} option={option} onEvents={onEvents} />
  );
};

export default LineGraph;
